import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { FrameworkContractOut } from 'src/app/services/rest-api/rest-api';
import tippy, { Instance, Props, SingleTarget } from 'tippy.js';

@Component({
  selector: 'libero-dip-picker',
  templateUrl: './dip-picker.component.html',
  styleUrls: ['./dip-picker.component.scss'],
  standalone: false
})
export class DipPickerComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() public availableContracts!: FrameworkContractOut[];
  @Input() public formName!: AbstractControl;
  @Input() public type: 'new-strategy' | 'new-portfolio' = 'new-portfolio';
  @Input() public efektikaLimitedDip = false;

  @ViewChild('tooltipContent') public tooltipContent?: ElementRef;
  private tooltipHoverable?: ElementRef;
  @ViewChild('tooltipHoverableRadio1') private contentRadio1!: ElementRef;
  @ViewChild('tooltipHoverableRadio2') private contentRadio2!: ElementRef;
  @ViewChild('tooltipHoverableSwitch') private contentSwitch!: ElementRef;
  private tooltipInstance: Instance<Props> | null = null;

  public get displayEfektikaTooltip(): boolean {
    return this.efektikaLimitedDip && this.availableContracts.length > 1;
  }

  public hideDipSwitch = false;
  public hideContractSwitch = true;
  public dipDisabled = false;

  public switchForm!: UntypedFormGroup;
  private formSubscription?: Subscription;

  private get dipContract() {
    return this.availableContracts.find(contract => contract.dip);
  }

  public ngOnInit(): void {
    this.initForm();
    this.initiateValue();
  }

  private initForm(): void {
    this.switchForm = new UntypedFormGroup({
      dip: new UntypedFormControl(this.formName.value.dip),
      contracts: new UntypedFormControl(false),
    });

    this.formSubscription = this.switchForm.valueChanges.pipe(distinctUntilChanged()).subscribe( changes => this.updateParentForm(changes) );
  }

  private initiateValue(): void {
    if(this.dipContract && this.availableContracts.length === 1) {
      // only dip
      this.switchForm.controls['dip'].patchValue(true);
      this.dipDisabled = true;
      this.formName.patchValue(this.dipContract);
    } else if (this.availableContracts.length === 1) {
      // only one non-dip
      this.hideDipSwitch = true;
      this.hideContractSwitch = true;
    } else if(this.availableContracts.length > 1 && !this.dipContract) {
      // two non-dip select && one dip one non-dip
      this.hideDipSwitch = true;
      this.hideContractSwitch = false;
    }
  }

  private updateParentForm(changes: unknown): void {
    if(
      typeof changes === 'object' &&
      changes !== null &&
      'dip' in changes &&
      changes.dip
    ) {
      this.formName.patchValue(this.dipContract);
    } else {
      const switched = this.availableContracts.find(contract => contract.frameworkContractId !== this.formName.value.frameworkContractId);
      this.formName.patchValue(switched);
    }
  }

  public updateRadioValue(frameworkContractId: number): void {
    const switched = this.availableContracts.find(contract => contract.frameworkContractId === frameworkContractId);
    this.formName.patchValue(switched);
  }

  private initTooltip(): void {
    if(this.tooltipInstance) {
      this.tooltipInstance.destroy();
      this.tooltipInstance = null;
    }
    if(this.tooltipHoverable && this.tooltipContent) {
      this.tooltipInstance = tippy(this.tooltipHoverable.nativeElement as SingleTarget, {
        content: this.tooltipContent.nativeElement.innerHTML,
        allowHTML: true,
        placement: 'auto',
      }) as Instance<Props>;
    }
  }

  private determineHoverableForTooltip(): void {
    if (this.efektikaLimitedDip) {
      if (
        // first radio cannot be chosen
        this.contentRadio1 && this.formName.value.frameworkContractNumber !== this.availableContracts[0].frameworkContractNumber
      ) {
        this.tooltipHoverable = this.contentRadio1;
      } else if (
        // or second radio cannot be chosen
        this.contentRadio2 && this.formName.value.frameworkContractNumber !== this.availableContracts[1].frameworkContractNumber
      ) {
        this.tooltipHoverable = this.contentRadio2;
      } else if (this.contentSwitch) {
        // or switch cannot be toggled
        this.tooltipHoverable = this.contentSwitch;
      }
      if (this.tooltipHoverable) {
        this.initTooltip();
      }
    }
  }

  public ngAfterViewInit(): void {
    this.determineHoverableForTooltip();
  }

  public ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
  }
}
